import { useState, useCallback } from 'react';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { GetApp } from '@mui/icons-material';

import Button from 'components/library/Button/Button';
import IbanPDF from './IbanPDF';

import styles from './Transfer.module.css';
import type { IbanTransfer } from 'types/Iban';

const MAX_RETRY_ATTEMPTS = 3;

export default function Transfer({ transfer }: { transfer: IbanTransfer }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [retryCount, setRetryCount] = useState(0);
  const [key, setKey] = useState(0);

  const handleRetry = useCallback(() => {
    if (retryCount < MAX_RETRY_ATTEMPTS - 1) {
      setRetryCount(prevCount => prevCount + 1);
      setKey(prevKey => prevKey + 1);
    } else {
      setRetryCount(MAX_RETRY_ATTEMPTS);
    }
  }, [retryCount]);

  return (
    <div className={styles.transfer}>
      <p>
        {t('payment.transfert.description')}
        {retryCount < MAX_RETRY_ATTEMPTS ? (
          <PDFDownloadLink
            document={<IbanPDF transfer={transfer} t={t} />}
            fileName="iban.pdf"
            className={styles.download}
            key={key}
          >
            {({ loading, error }) => {
              if (loading) {
                return t('payment.transfert.ongoing');
              }
              if (retryCount === MAX_RETRY_ATTEMPTS) {
                return t('payment.transfert.error');
              }
              if (error) {
                setTimeout(handleRetry, 1000);
                return null;
              }
              return (
                <>
                  <GetApp className={styles.icon} />
                  {t('payment.transfert.download')}
                </>
              );
            }}
          </PDFDownloadLink>
        ) : (
          <span className={styles.download}>{t('payment.transfert.error')}</span>
        )}
      </p>
      <Iban
        transfer={transfer}
        name={transfer.display_bank_transfer_instructions.financial_addresses[0].iban.account_holder_name}
        t={t}
      />
      <Button
        type="secondary"
        variant="fill"
        label={t('payment.transfert.doneButton')}
        onButtonClick={() => navigate(`/downloadInvoice`)}
        className={styles.submit}
      />
    </div>
  );
}

const Iban = ({ name, transfer, t }: {
  name: string,
  transfer: IbanTransfer,
  t: (key: string, options?: Record<string, any>) => string;
}) => (
  <div className={styles.grid}>
    <p className={styles.title}>
      {t('payment.transfert.amount')} :
    </p>
    <p className={styles.text}>
      {`${transfer.display_bank_transfer_instructions.amount_remaining} ${transfer.display_bank_transfer_instructions.currency === 'eur' ? '€' : transfer.display_bank_transfer_instructions.currency}`}
    </p>
    <p className={styles.title}>
      {t('payment.transfert.bank')} :
    </p>
    <p className={styles.text}>
      {name}
    </p>
    <p className={styles.title}>
      {t('payment.transfert.iban')} :
    </p>
    <p className={styles.text}>
      {transfer.display_bank_transfer_instructions.financial_addresses[0].iban.iban.match(/.{1,4}/g)?.join('  ')}
    </p>
    <p className={styles.title}>
      {t('payment.transfert.bic')} :
    </p>
    <p className={styles.text}>
      {transfer.display_bank_transfer_instructions.financial_addresses[0].iban.bic}
    </p>
  </div>
);
